import {
  createTopic,
  fetchCategoryPosts,
  fetchPostsByTags,
  resetError,
  resetPosts,
  searchFilter,
} from "../../redux/features/postsSlice";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { showCustomToast } from "../../utils/toastUtil";
import { useDispatch, useSelector } from "react-redux";
import "react-quill/dist/quill.snow.css";
import PostItem from "../Home/PostItem";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCategories,
  fetchTags,
  searchTags,
} from "../../helpers/querycalls";
import _, { debounce } from "lodash";

import { getQuillModules, imageHandler } from "../../utils/quillUtil";
import { CustomReactQuill } from "../Editor/CustomReactQuill";
import PostShimmer from "../partials/postShimmer";
import { Search } from "../partials/Search";
import { ShimmerBadge } from "react-shimmer-effects";
import NotFound from "../NotFound";
import { Helmet } from "react-helmet";
import { AnimatePresence, motion } from "framer-motion";

const CategoryPosts = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const tagsDropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  // =======================
  // Redux State Management
  // =======================
  const {
    posts,
    status: postsStatus,
    loading: postsLoading,
    filterTopTags,
    arePostsLeft,
  } = useSelector((state) => state.posts);

  // =======================
  // useParams
  // =======================
  const { categoryId } = useParams();

  // =======================
  // framer motion
  // =======================

  const options = {
    initial: { y: "100vh" },
    animate: { y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { y: "100vh", transition: { duration: 0.3, ease: "easeIn" } },
  };
  const popupAntimation = {
    initial: { y: 30, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: { y: 30, opacity: 0, transition: { duration: 0.2, ease: "easeIn" } },
  };
  // =======================
  // useStates
  // =======================
  const [formVisible, setFormVisible] = useState(false);
  const [filteringTags, setFilteringTags] = useState(false);
  const [filterSelected, setFilterSelected] = useState("new");
  const [newPost, setNewPost] = useState({
    title: "",
    category: categoryId,
    raw: "",
  });
  const [filteredPosts, setFilteredPosts] = useState(
    posts.filter((post) => post)
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isTabsDropDownOpen, setIsTabsDropDownOpen] = useState(false);
  const [postTags, setPostTags] = useState(null);
  const [selectedTag, setSelectedTag] = useState([]);
  const [modalMaximized, setModalMaximized] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchText, setSearchText] = useState("");
  const handleSearchTags = (e) => {
    setSearchText(e.target.value);
    dispatch(searchFilter(e.target.value));
  };
  const handleClearTags = () => {
    setSearchText("");
    dispatch(searchFilter(""));
  };

  const debouncedHandleTagSearch = debounce((value) => {
    handleTagSearch(value);
  }, 300);

  const handleTagSearch = async (query) => {
    setFilteringTags(true);
    const result = await searchTags(query);
    setFilteringTags(false);
    if (result) {
      setPostTags(result);
    }
  };

  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);

  const [solved, setSolved] = useState(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [option, setOption] = useState("الأحدث");
  const toggleDropdown = () => setIsOpen(!isOpen);
  const handleSelect = (option, filter, solved) => {
    setOption(option);
    setIsOpen(false);
    handleChangeFilterSelected(filter, solved);
  };

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { data: tags } = useQuery({
    queryKey: ["tags"],
    queryFn: fetchTags,
    retry: false,
    enabled: !!formVisible,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (tags) {
      setPostTags(tags);
    }
  }, [tags]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // =======================
  // Computed States
  // =======================
  const singleCategory =
    categories?.find((category) => category.id === Number(categoryId)) ||
    categories
      ?.flatMap((category) => category.subcategory_list || [])
      .find((sub) => sub.id === Number(categoryId));

  // =======================
  // useRefs
  // =======================
  const modalRef = useRef(null);
  const quillRef = useRef();
  const arePostsLeftRef = useRef(arePostsLeft);
  const toggleDiv = (e) => {
    e.stopPropagation();
    setFiltersOpen((prev) => !prev);
  };
  const handleClickOutside = (event) => {
    if (
      filterMenuRef.current &&
      !filterMenuRef.current.contains(event.target)
    ) {
      setFiltersOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const modules = useMemo(
    () => getQuillModules(quillRef, imageHandler),
    [quillRef]
  );

  const handleScrollBottomTouch = () => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (scrollHeight - scrollTop - clientHeight <= 5) {
      if (arePostsLeftRef.current) {
        if (selectedTags.length > 0) {
          dispatch(fetchPostsByTags(categoryId, selectedTags));
        } else if (solved !== undefined) {
          let filterType = "latest";
          dispatch(fetchCategoryPosts(categoryId, filterType, solved));
        } else {
          dispatch(fetchCategoryPosts(categoryId, filterSelected));
        }
      }
    }
  };
  const debouncedHandleScrollBottomTouch = _.debounce(
    handleScrollBottomTouch,
    300
  );
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTabsDropDownOpen(false); // Close the dropdown if you click outside it
      }
    };

    if (isTabsDropDownOpen) {
      document.addEventListener("mousedown", handleClickOutside); // Add listener to window when dropdown is open
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up listener if it's closed
    };
  }, [isTabsDropDownOpen]);
  useEffect(() => {
    window.addEventListener("scroll", debouncedHandleScrollBottomTouch);

    return () => {
      window.removeEventListener("scroll", debouncedHandleScrollBottomTouch);
    };
  }, [
    categoryId,
    filterSelected,
    selectedTags,
    posts,
    postsStatus,
    debouncedHandleScrollBottomTouch,
  ]);
  const handleChangeTags = (tag) => {
    setSelectedTags((prevTags) => {
      if (!prevTags.includes(tag)) {
        const updatedTags = [...prevTags, tag]; // Add new tag
        handleSelectChange(updatedTags); // Pass updated tags
        return updatedTags;
      }
      return prevTags; // Prevent duplicates
    });
  };
  const handleRemoveTag = (tag) => {
    setSelectedTags((prevTags) => {
      const updatedTags = prevTags.filter((t) => t !== tag); // Remove tag
      handleSelectChange(updatedTags); // Pass updated tags
      return updatedTags;
    });
  };

  // =======================
  // User Authentication
  // =======================

  // Admin/normal user logic
  const userObj = localStorage.getItem("salla_discourse_user");
  const user = JSON.parse(userObj);
  let should_create_post = false;
  let isLoggedin = false;
  if (user) {
    isLoggedin = true;
    if (user.admin) {
      // Success criteria for admin
      should_create_post = true;
    } else if (singleCategory && !singleCategory.only_admin_can_post) {
      // Success criteria for normal user
      should_create_post = true;
    }
  }

  // =======================
  // useEffects
  // =======================

  // Update the ref whenever `arePostsLeft` changes
  useEffect(() => {
    arePostsLeftRef.current = arePostsLeft;
  }, [arePostsLeft]);

  // Fetch posts when categoryId or filter changes
  useEffect(() => {
    dispatch(resetPosts());
    setFilterSelected("new");
    dispatch(fetchCategoryPosts(categoryId, "new"));
  }, [dispatch, categoryId]);

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 130) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const handleClose = useCallback(() => {
    setSelectedTag([]);
    setNewPost((prev) => ({ ...prev, title: "", raw: "" }));
    setFormVisible(false);
    dispatch(resetError());
  }, [dispatch]); // Add dependencies (only ones that change)

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !modalMaximized &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !event.target.closest(".custom-toast")
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose, modalMaximized]);

  // Handle filtered posts update
  useEffect(() => {
    setFilteredPosts(posts);
  }, [posts]);

  // =======================
  // useMemos
  // =======================

  // =======================
  // useCallbacks
  // =======================
  const handleSelectChange = useCallback(
    (selectedTags) => {
      // setFilterSelected("latest");//why?
      dispatch(resetPosts());
      if (selectedTags.length > 0) {
        dispatch(fetchPostsByTags(categoryId, selectedTags));
      } else {
        dispatch(fetchCategoryPosts(categoryId, filterSelected));
      }
    },
    [categoryId, filterSelected, dispatch]
  );
  // =======================
  // Handlers
  // =======================
  async function handleFormSubmit(e) {
    e.preventDefault();
    try {
      if (!selectedTag.length) {
        showCustomToast("يرجى اضافه الوسم", "error");
        return;
      }
      const editor = quillRef.current.getEditor();
      let htmlValue = editor.root.innerHTML; // This will give you the HTML content

      await dispatch(
        createTopic({
          ...newPost,
          raw: htmlValue,
          tag: selectedTag,
          category: categoryId,
        })
      );
      queryClient.invalidateQueries(["tags"]);
      if (!postsLoading) {
        setFormVisible(false);
      }
    } catch (err) {
      console.log(err);
    }
  }
  const handleClearAll = () => {
    setSelectedTags([]);
    handleSelectChange([]);
  };
  // Handle change in selected filter
  function handleChangeFilterSelected(filter, solved) {
    dispatch(resetPosts());
    filter += solved !== undefined ? (solved ? "_unsolved" : "_solved") : "";
    setFilterSelected(filter);
    setSolved(solved);
    let filterType = solved !== undefined ? "latest" : filter;
    dispatch(fetchCategoryPosts(categoryId, filterType, solved));
  }

  // Handle Quill editor value change
  const handlePostCreation = () => {
    if (!isLoggedin) {
      // navigate("/login");
      window.location.href =
        process.env.REACT_APP_API_URL + `/auth/oauth2_basic`;
      return false;
    }
    if (!should_create_post) {
      showCustomToast("غير مصرح لك بتنفيذ هذا الإجراء", "error");
      return false;
    }
    setFormVisible(true);
  };

  // =======================
  // Togglers
  // =======================
  const toggleTagDropdown = () => {
    setIsTagDropdownOpen((prev) => !prev);
  };
  if (!isLoading && !singleCategory && !postsLoading) {
    return <NotFound />;
  }

  return (
    <>
      <Helmet>
        <title>{singleCategory?.name}</title>
        <meta name="description" content={singleCategory?.description_text} />
      </Helmet>
      <div className="flex md:justify-normal justify-end w-full ">
        <AnimatePresence>
          {formVisible && (
            <div className="fixed inset-0 z-50 flex justify-center items-end bg-opacity-20">
              <motion.div
                variants={options}
                initial="initial"
                animate="animate"
                exit="exit"
                ref={modalRef}
                style={{ boxShadow: "0px 10px 100px 12px rgba(0, 0, 0, 0.1)" }}
                className={`relative flex flex-col   ${
                  modalMaximized
                    ? "w-full h-full"
                    : "sm:w-[1220px] h-full sm:h-auto w-full"
                } rounded-lg bg-[#fbfdfe] shadow-lg overflow-hidden`}
                dir="rtl"
              >
                <div className="px-4 md:px-10 pt-8 top-8 flex justify-between">
                  <div className="flex gap-5 items-center">
                    <img
                      src="/images/modalpen.png"
                      alt=""
                      className="cursor-pointer h-[48px]"
                    />
                    <span className="text-[20px] sm:text-[24px] font-medium sm:font-bold my-0.5">
                      منشور جديد
                    </span>
                  </div>

                  <div className="flex gap-5 sm:gap-10">
                    <img
                      src="/images/expand.svg"
                      alt=""
                      className="sm:block hidden cursor-pointer w-[24px] h-[24px]"
                      onClick={() => setModalMaximized((prev) => !prev)}
                    />
                    <img
                      src="/images/home/cross.svg"
                      alt=""
                      className="cursor-pointer w-[24px] h-[24px]"
                      onClick={() => {
                        setModalMaximized(false);
                        handleClose();
                      }}
                    />
                  </div>
                </div>
                <hr
                  className="mt-5"
                  style={{
                    height: "1px",
                    borderWidth: 0,
                    color: "#F1F8F9",
                    backgroundColor: "#F1F8F9",
                  }}
                />
                <div
                  className={`overflow-y-auto pt-5 sm:max-h-[85svh] custom-scrollbar scroll-ms-4 md:px-10 px-4 ${
                    modalMaximized ? "h-full" : ""
                  } sm:h-auto h-full`}
                >
                  <form
                    className={`flex md:flex-row flex-col gap-x-11 ${
                      modalMaximized ? "justify-center" : ""
                    }`}
                    onSubmit={handleFormSubmit}
                  >
                    <div className="flex flex-col md:max-w-[240px] mt-2.5">
                      <p className="text-[#444444] text-[15px] font-medium">
                        اكتب موضوع جديد في
                      </p>
                      <div class="flex w-fit mt-2 py-[8px] px-[10px] gap-1 justify-center items-center bg-[#E4FAF5] border-[1px] border-[#76E8CD] text-[#004D5A] rounded-lg">
                        <img
                          src={
                            process.env.REACT_APP_API_URL +
                            singleCategory?.uploaded_logo.url
                          }
                          alt="emoji"
                          className=" w-5 h-5"
                        />
                        <span class=" text-[16px]">{singleCategory?.name}</span>
                      </div>
                      <div className="md:mt-12 mt-8">
                        {user.admin && (
                          <div className="flex justify-between text-medium text-[#444444]">
                            <span>سهّل البحث عن منشورك عبر إضافة وسوم</span>
                          </div>
                        )}
                        <div
                          className="cursor-pointer mt-[8px]  border-[1px] border-[#DDDDDD] rounded-[4px] flex gap-5 py-[10px] px-[20px] items-center"
                          onClick={toggleTagDropdown}
                        >
                          <img src="/images/home/tag.svg" alt="Tag" />
                          <input
                            type="text"
                            onChange={(e) => {
                              if (e.target.value.length > 0) {
                                debouncedHandleTagSearch(e.target.value);
                              } else {
                                setPostTags(tags);
                              }
                            }}
                            className="text-[14px] placeholder:text-[#BBBBBB] outline-none border-none w-full bg-transparent"
                            placeholder="أضف وسوم مناسبة"
                          />
                        </div>

                        <div
                          className="flex flex-wrap gap-2 mt-5"
                          style={
                            isTagDropdownOpen
                              ? { display: "none" }
                              : { display: "flex" }
                          }
                        >
                          {selectedTag?.map((tag) => (
                            <div className="bg-[#BAF3E6] text-[#004D5A] px-1.5 py-0.5 flex gap-2 rounded-full text-[14px] items-center">
                              <p>{tag.name}</p>
                              <img
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedTag((prevTags) =>
                                    prevTags.filter((t) => t.id !== tag.id)
                                  );
                                }}
                                src="/images/remove-tag.svg"
                                alt="X"
                              />
                            </div>
                          ))}
                        </div>

                        {isTagDropdownOpen && postTags.length > 0 && (
                          <div
                            ref={tagsDropdownRef}
                            className="p-1 mt-2 border-[1px] border-[#DDDDDD] bg-white rounded-md shadow-md"
                          >
                            <div className="overflow-auto max-h-[142px] scrollbar-thiner">
                              {filteringTags ? (
                                <div className="flex justify-center items-center h-[142px]">
                                  <img
                                    loading="eager"
                                    src="/images/salla_loader_big.svg"
                                    alt="Loading..."
                                    className="w-[32px] h-[32px] loader self-center"
                                  />
                                </div>
                              ) : (
                                <ul>
                                  {postTags.map((tag) => (
                                    <li
                                      key={tag.id}
                                      className="px-[20px] py-[10px] cursor-pointer hover:bg-gray-200 rounded-sm transition-colors me-1"
                                      onClick={() => {
                                        setSelectedTag((prevTags) => {
                                          if (
                                            !prevTags.some(
                                              (t) => t.id === tag.id
                                            )
                                          ) {
                                            return [
                                              ...prevTags,
                                              { name: tag.value, id: tag.id },
                                            ];
                                          }
                                          return prevTags;
                                        });
                                        setIsTagDropdownOpen(false);
                                      }}
                                    >
                                      {tag.value}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col rounded-lg border-[#EEE] border max-w-[780px] w-full shadow-md max-md:mt-6">
                      <div>
                        <input
                          required={true}
                          type="text"
                          id="title"
                          name="title"
                          value={newPost.title}
                          onChange={(e) =>
                            setNewPost({ ...newPost, title: e.target.value })
                          }
                          className=" h-[54px] p-2 px-5 w-full border-b-0 rounded-lg rounded-b-none placeholder:text-[#BBBBBB]"
                          placeholder="اكتب عنوان لمنشورك"
                        />
                        <CustomReactQuill
                          quillRef={quillRef}
                          modules={modules}
                          placeholder="شارك أفكارك، أسئلتك، أو أيّ موضوع يهم مجتمع سلة."
                        />
                      </div>
                      <div className="flex justify-end gap-4 py-8 px-10">
                        <button
                          type="submit"
                          className="transition-colors btn bg-blue-500 px-6 py-2.5 text-white rounded-[4px]"
                        >
                          أضف المنشور
                        </button>
                      </div>
                    </div>
                  </form>
                  <p className="text-center text-[#999999] text-base font-normal my-20">
                    اطّلع على ضوابط المشاركة؛ للمحافظة على نقاشات مُثرية وملهمة
                    تترك أثرًا إيجابيًا في مجتمع سلة
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <div className="flex-1" dir="rtl">
          <div className=" w-full">
            <div className=" w-full mt-4">
              <div
                className={`flex flex-col px-3 items-center justify-between py-3 sm:flex-col  sm:h-[124px]  bg-[#F8F8F8] sm:mb-[6rem] z-10 `}
              >
                {!isLoading && (
                  <div
                    className={`text-[#004D5A] duration-300 md:text-[48px] text-3xl md:mb-7 mb-5 flex items-center justify-center gap-2 sm:mr-0 mr-4 font-bold text-center `}
                  >
                    {singleCategory?.uploaded_logo ? (
                      <img
                        src={
                          process.env.REACT_APP_API_URL +
                          singleCategory?.uploaded_logo.url
                        }
                        alt="emoji"
                        className=" md:w-12 w-8 h-8  md:h-12"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/logo.svg";
                        }}
                      />
                    ) : (
                      <img
                        src={"/images/logo.svg"}
                        alt="emoji"
                        className=" w-12"
                      />
                    )}
                    {singleCategory?.name}{" "}
                  </div>
                )}

                {isLoading && (
                  <div
                    className={`text-[#004D5A] duration-300 md:text-[48px] text-3xl md:mb-7 mb-5 flex items-center justify-center gap-4 sm:mr-0 mr-4 font-bold text-center `}
                  >
                    <ShimmerBadge width={50}></ShimmerBadge>
                    <ShimmerBadge width={150}></ShimmerBadge>
                  </div>
                )}
                <h3 className="mb-3 text-[#666666] md:text-start text-center">
                  {singleCategory?.description_text}
                </h3>
                <div className={` w-full ${isScrolled ? "hidden" : "block"}`}>
                  <Search
                    type="local"
                    placeholder="ابحث عن سؤالك هنا"
                    setFormVisible={setFormVisible}
                  />
                </div>
              </div>
              <div className="w-[95%] h-[1px] bg-[#eee] !mt-[60px] "></div>
              <div className="sm:px-6">
                <div className=" sm:pt-10 pb-3 sticky top-[87px] pt-3 sm:top-[5rem] bg-[#f8f8f8]  ">
                  <div className="max-w-[1000px] lg:mx-auto max-sm:mx-4 flex xl:flex-nowrap flex-wrap gap-3 lg:justify-between justify-center">
                    <span
                      className={`text-[#004D5A] duration-300  items-center gap-2 text-[32px] mb-[10px] sm:mr-0 mr-4  font-bold text-center ${
                        isScrolled ? "md:flex hidden" : "hidden"
                      } `}
                    >
                      {singleCategory?.uploaded_logo ? (
                        <img
                          src={
                            process.env.REACT_APP_API_URL +
                            singleCategory?.uploaded_logo.url
                          }
                          alt="emoji"
                          className=" w-10  "
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/logo.svg";
                          }}
                        />
                      ) : (
                        <img
                          src={"/images/logo.svg"}
                          alt="emoji"
                          className=" w-12  "
                        />
                      )}
                      {singleCategory?.name}{" "}
                    </span>
                    <div
                      className={` gap-10 items-center  hidden ${
                        isScrolled ? "hidden" : "lg:flex hidden"
                      }`}
                    >
                      <span
                        className={`${
                          filterSelected === "new"
                            ? "text-[#333333] border-b-[2px] pb-2 border-[#999999] cursor-pointer font-medium"
                            : "text-[#666666] cursor-pointer pb-2"
                        } lg:text-base text-lg`}
                        onClick={() => handleChangeFilterSelected("new")}
                      >
                        الأحدث
                      </span>
                      <span
                        className={`${
                          filterSelected === "latest"
                            ? "text-[#333333] border-b-[2px] pb-2 border-[#999999] cursor-pointer font-medium"
                            : "text-[#666666] cursor-pointer pb-2"
                        } lg:text-base text-lg`}
                        onClick={() => handleChangeFilterSelected("latest")}
                      >
                        الأكثر تفاعل
                      </span>
                    </div>
                    <div className="flex justify-center items-center gap-[10px]">
                      <div
                        className={`relative ${
                          isScrolled ? "block" : "hidden"
                        } max-md:block`}
                      >
                        {/* Select Box */}
                        <button
                          type="button"
                          className=" sm:hidden border-[1px] text-[14px] text-[#666] py-4 font-medium border-[#DDDDDD] flex justify-center sm:gap-6 gap-3 cursor-pointer items-center w-[180px]  h-[42px] rounded-[4px]"
                          onClick={toggleDropdown}
                        >
                          <span className="text-[14px] md:text-[#666] text-[#004d5a] flex justify-center items-center gap-2">
                            ترتيب حسب: {option}
                          </span>
                          <img
                            src="/images/arrow-down.png"
                            alt="arrow-icon"
                            className={`${isOpen ? "rotate-180" : "rotate-0"}`}
                          />
                        </button>

                        {/* Dropdown List */}
                        <AnimatePresence>
                          {isOpen && (
                            <motion.ul
                              variants={popupAntimation}
                              initial="initial"
                              animate="animate"
                              exit="exit"
                              className="absolute md:block hidden  w-full bg-white  max-h-32 overflow-y-auto z-10 scrollbar-thiner  py-2 border border-gray-300 rounded-md shadow-lg focus:outline-none  sm:text-sm"
                            >
                              <li
                                className={`p-2 text-[#707070] cursor-pointer hover:bg-[#baf3e6] hover:text-[#004d5a] `}
                                onClick={() => handleSelect("الأحدث", "new")}
                              >
                                الأحدث
                              </li>
                              <li
                                className={`p-2 text-[#707070] cursor-pointer hover:bg-[#baf3e6] hover:text-[#004d5a] `}
                                onClick={() =>
                                  handleSelect("الأكثر تفاعل", "latest")
                                }
                              >
                                الأكثر تفاعل
                              </li>
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </div>
                      <div ref={filterMenuRef} className=" relative">
                        <button
                          id="filterButton"
                          className={`sm:hidden border-[1px] ${
                            selectedTags.length > 0
                              ? "border-[#004d5a] text-[#004d5a] md:w-[200px]"
                              : "border-[#DDDDDD] md:text-[#666] text-[#004d5a] "
                          }   flex justify-center sm:gap-6 gap-3 cursor-pointer items-center md:w-[192px] w-[165px] h-[42px] rounded-[4px]`}
                          onClick={toggleDiv}
                        >
                          {selectedTags.length > 0 ? (
                            <>
                              <span className="text-[14px] flex items-center gap-2 justify-center  py-4 font-medium">
                                تصنيف المنشورات{" "}
                                <span className="md:w-6 md:h-6 w-4 h-4 md:flex hidden bg-[#004d5a] rounded-full  justify-center items-center">
                                  <span className=" text-white md:text-base text-[14px] -mb-1">
                                    {selectedTags.length}
                                  </span>
                                </span>
                              </span>
                              <img
                                src="/images/arrow-down-01.png"
                                alt="arrow-icon"
                              />
                            </>
                          ) : (
                            <>
                              <span className="text-[14px]  py-4 font-medium">
                                تصنيف المنشورات
                              </span>
                              <img
                                src="/images/arrow-down.png"
                                alt="arrow-icon"
                              />
                            </>
                          )}
                        </button>
                        <AnimatePresence>
                          {filtersOpen && (
                            <motion.div
                              variants={popupAntimation}
                              initial="initial"
                              animate="animate"
                              exit="exit"
                              className="absolute top-[52px] right-6 md:block hidden  bg-white w-[385px]  h-[348px] overflow-y-auto z-10 select-scrollbar   border border-gray-300 rounded-md shadow-xl focus:outline-none  sm:text-sm"
                            >
                              <div className=" relative">
                                <input
                                  type="text"
                                  className=" w-full h-[54px] mb-[10px] relative focus-within:outline-none  border-b border-[#eee] focus:border-b focus:border-gray-500 pe-2 text-right ps-12 placeholder:ps-4 placeholder:text-base placeholder:text-[#707070] placeholder:font-normal"
                                  placeholder="ابحث عن وسم معين"
                                  style={{
                                    backgroundImage:
                                      "url('/images/sidebar/search.svg')",
                                    backgroundSize: "20px 20px",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "95% center",
                                  }}
                                  value={searchText}
                                  onChange={handleSearchTags}
                                />
                                <img
                                  src="/images/multiplication-sign.png"
                                  alt="icon"
                                  className=" absolute top-4 left-4 cursor-pointer"
                                  onClick={handleClearTags}
                                />
                              </div>

                              <div className="px-[10px] flex gap-2 flex-wrap items-center">
                                {filterTopTags?.map((t, index) => (
                                  <div
                                    key={index}
                                    className={`${
                                      selectedTags.includes(t)
                                        ? "border-[#eefcf9] bg-[#eefcf9]"
                                        : "border-[#eee] cursor-pointer"
                                    } rounded-[100px] border flex items-center gap-2 justify-center text-sm text-[#666] px-[10px] py-[6px] `}
                                    onClick={() => handleChangeTags(t)}
                                  >
                                    <span>{t.value}</span>
                                    {selectedTags.includes(t) && (
                                      <img
                                        className="cursor-pointer"
                                        src="/images/remove-tag.svg"
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent triggering handleChangeTags
                                          handleRemoveTag(t);
                                        }}
                                        alt="close"
                                      />
                                    )}
                                  </div>
                                ))}
                                {filterTopTags.length === 0 && (
                                  <p className="p-2 text-sm text-[#999]">
                                    لا توجد نتائج
                                  </p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <button
                        type="submit"
                        className={`hidden sm:block btn px-8 py-3 text-[14px] font-medium rounded-[4px] text-[#baf3e6]  w-[185px] h-[42px]`}
                        onClick={() => {
                          if (!isLoggedin) {
                            // navigate('/login');
                            window.location.href =
                              process.env.REACT_APP_API_URL +
                              `/auth/oauth2_basic`;
                          } else {
                            handlePostCreation();
                          }
                        }}
                      >
                        + أضف سؤال جديد
                      </button>
                    </div>
                  </div>
                </div>
                {selectedTags.length > 0 && (
                  <div className=" flex flex-wrap items-center gap-2 py-5 border-b border-t max-w-[1000px] lg:mx-auto max-sm:mx-4 border-[#eee]">
                    {selectedTags.map((t, index) => (
                      <button
                        className=" px-[14px] py-[2px] rounded-[100px] flex items-center gap-2 bg-[#baf3e6] text-[#004D5A] text-sm font-normal"
                        key={index}
                      >
                        <span>{t.value}</span>
                        <img
                          className="cursor-pointer"
                          src="/images/remove-tag.svg"
                          onClick={() => {
                            handleRemoveTag(t);
                          }}
                          alt="close"
                        />
                      </button>
                    ))}
                    <button className="px-[14px] py-[2px] rounded-[100px] flex items-center gap-2 bg-[#eee] text-[#444] text-sm font-normal">
                      <span>إلغاء تحديد الكل</span>
                      <img
                        className="cursor-pointer"
                        src="/images/close-line.png"
                        onClick={() => {
                          handleClearAll();
                          setFiltersOpen(false);
                        }}
                        alt="close"
                      />
                    </button>
                  </div>
                )}

                <div className="mt-2 max-w-[1000px] lg:mx-auto max-sm:mx-4">
                  {/* post shimmers only */}
                  {postsStatus === "fetching" ? (
                    <div className="flex flex-col justify-center gap-y-2">
                      {[...Array(6)].map((_, index) => (
                        <PostShimmer key={index} />
                      ))}
                    </div>
                  ) : filteredPosts.length > 0 ? (
                    filteredPosts.map((post) => (
                      <React.Fragment key={post.id}>
                        <PostItem
                          post={post}
                          isLoggedIn={isLoggedin}
                          loggedInUserId={user?.id}
                        />
                      </React.Fragment>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center mt-10 gap-4">
                      <p
                        dir="ltr"
                        className="text-black text-[80px] text-center"
                      >
                        :(
                      </p>
                      <p className="text-black text-[14px] text-center">
                        لا توجد نتائج
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!formVisible && (
        <div className="fixed bottom-4 right-4 z-49 sm:hidden">
          <button
            onClick={() => {
              if (!isLoggedin) {
                // navigate('/login');
                window.location.href =
                  process.env.REACT_APP_API_URL + `/auth/oauth2_basic`;
              } else {
                handlePostCreation();
              }
            }}
            className="btn bg-[#BAF3E6] text-white rounded-full p-6 shadow-lg focus:outline-none"
          >
            <img src="/images/home/plus.svg" alt="" />
          </button>
        </div>
      )}
      <AnimatePresence>
        {isOpen && isMobile && (
          <div
            style={{ direction: "rtl" }}
            className="flex  md:flex-row flex-col max-md:justify-end md:gap-10 gap-5 items-center md:text-[18px] text-[15px] max-md:fixed inset-0 max-md:bg-[#004D5A] !bg-opacity-50 max-md:border md:rounded-lg md:h-fit h-[100dvh] z-[99999]"
            onClick={toggleDropdown}
          >
            <motion.div
              initial={{ y: "100vh" }}
              animate={{ y: 0, transition: { duration: 0.3 } }}
              exit={{ y: "100vh", transition: { duration: 0.3 } }}
              ref={dropdownRef}
              className=" flex flex-col bg-white w-full rounded-t-[10px] p-4 gap-y-4"
            >
              <img
                src="/images/bottom-sheet-anchor.svg"
                alt="anchor"
                className="mb-4 self-center"
              />
              <span
                className={
                  option === "الأحدث"
                    ? `text-[#333333] border-[#999999] cursor-pointer flex gap-3 items-center text-medium`
                    : ` text-medium text-[#666666] cursor-pointer ms-10`
                }
                onClick={() => {
                  handleSelect("الأحدث", "new");
                }}
              >
                <img
                  className={option === "الأحدث" ? `block` : `hidden`}
                  src="/images/selected.svg"
                  alt=""
                />{" "}
                الأحدث
              </span>
              <hr className="-mx-4" />
              <span
                className={
                  option === "الأكثر تفاعل"
                    ? `text-[#333333] border-[#999999] cursor-pointer flex gap-3 items-center text-medium`
                    : ` text-medium text-[#666666] cursor-pointer ms-10`
                }
                onClick={() => {
                  handleSelect("الأكثر تفاعل", "latest");
                }}
              >
                <img
                  className={option === "الأكثر تفاعل" ? `block` : `hidden`}
                  src="/images/selected.svg"
                  alt=""
                />{" "}
                الأكثر تفاعل
              </span>
              <hr className="-mx-4" />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {filtersOpen && isMobile && (
          <div
            onClick={toggleDiv}
            style={{ direction: "rtl" }}
            className="flex  md:flex-row flex-col max-md:justify-end lg:gap-10 gap-5 items-center lg:text-[18px] text-[15px] max-md:fixed inset-0 max-md:bg-[#004D5A] !bg-opacity-50 max-md:border md:rounded-lg md:h-fit h-[100dvh] z-[99999]"
          >
            <motion.div
              initial={{ y: "100vh" }}
              animate={{ y: 0, transition: { duration: 0.3 } }}
              exit={{ y: "100vh", transition: { duration: 0.3 } }}
              className=" flex flex-col z-[999999] bg-white w-full rounded-t-[10px] p-4 gap-y-4 min-h-[350px]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src="/images/bottom-sheet-anchor.svg"
                alt="anchor"
                className="mb-4 self-center"
              />
              <div className=" relative">
                <input
                  type="text"
                  className=" w-full h-[54px] mb-[10px] relative focus-within:outline-none  border-b border-[#eee] focus:border-b focus:border-gray-500 pe-2 text-right ps-12 placeholder:ps-4 placeholder:text-base placeholder:text-[#707070] placeholder:font-normal"
                  placeholder="ابحث عن وسم معين"
                  style={{
                    backgroundImage: "url('/images/sidebar/search.svg')",
                    backgroundSize: "20px 20px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "95% center",
                  }}
                  value={searchText}
                  onChange={handleSearchTags}
                />
                <img
                  src="/images/multiplication-sign.png"
                  alt="icon"
                  className=" absolute top-4 left-4 cursor-pointer z-10"
                  onClick={handleClearTags}
                />
              </div>

              <div className="px-[10px] flex gap-2 flex-wrap items-center">
                {filterTopTags?.map((t, index) => (
                  <div
                    key={index}
                    className={`${
                      selectedTags.includes(t)
                        ? "border-[#eefcf9] bg-[#eefcf9]"
                        : "border-[#eee] cursor-pointer"
                    } rounded-[100px] border flex items-center gap-2 justify-center text-sm text-[#666] px-[10px] py-[6px] `}
                    onClick={() => {
                      handleChangeTags(t);
                    }}
                  >
                    <span>{t.value}</span>
                    {selectedTags.includes(t) && (
                      <img
                        className="cursor-pointer"
                        src="/images/remove-tag.svg"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering handleChangeTags
                          handleRemoveTag(t);
                        }}
                        alt="close"
                      />
                    )}
                  </div>
                ))}
                {filterTopTags.length === 0 && (
                  <p className="p-2 text-sm text-[#999]">لا توجد نتائج</p>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CategoryPosts;
